import { useCallback, useContext, useRef } from 'react';

import { enqueueSnackbar, SnackbarKey } from 'notistack';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '@/hooks/redux-hooks';
import { saveDataHub } from '@/slices/dataHubSlice';

import { RootState } from '../../../../store';
import { ExportSettingsContext, ExportSettingsType } from '../ExportSettingsContext';

const useExportSettings = () => {
  const { setExportSettings } = useContext(ExportSettingsContext);
  const dispatch = useAppDispatch();
  const snackId = useRef<SnackbarKey>(undefined);
  const { dataHubs, activeDataHubId } = useSelector((state: RootState) => state.dataHub);
  const activeDataHub = dataHubs.find(dataHub => dataHub.id === activeDataHubId);

  const updateExportSettings = useCallback(
    (updatedSettings: ExportSettingsType) => {
      if (activeDataHub) {
        const id = enqueueSnackbar('Saving your changes, please wait...', {
          variant: 'loading',
          persist: true
        });
        snackId.current = id;

        dispatch(
          saveDataHub({
            ...activeDataHub,
            exportSettings: updatedSettings
          })
        )
          .unwrap()
          .then(() => {
            if (snackId.current === id) {
              snackId.current = enqueueSnackbar({
                variant: 'success',
                autoHideDuration: 5000
              });
            }
          })
          .catch(() => {
            snackId.current = enqueueSnackbar(
              "Oops! We couldn't save your changes. Please try again.",
              {
                variant: 'error',
                persist: true
              }
            );
          });
        setExportSettings(updatedSettings);
      }
    },
    [activeDataHub, dispatch, setExportSettings]
  );

  return {
    updateExportSettings
  };
};

export default useExportSettings;
